import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthProvider';
import { ArrowLeft } from 'lucide-react';
import { GenericView } from '../../components/common/table';
import Alert from '../../components/Alert/Alert';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import CreateCurrencyRateForm from './components/CreateCurrencyRateForm';
import ViewCurrencyRate from './components/ViewCurrencyRate';
import {
  getCurrency,
  getCurrencyRates,
  createCurrencyRate,
  updateCurrencyRate,
  deleteCurrencyRate
} from './api/currencyApi';

const CurrencyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const canCurrency = (key) => can(key, 'master-currency');

  const [currency, setCurrency] = useState(null);
  const [rates, setRates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRatesLoading, setIsRatesLoading] = useState(false);
  const [alert, setAlert] = useState(null);

  const [isRateModalOpen, setIsRateModalOpen] = useState(false);
  const [isRateViewModalOpen, setIsRateViewModalOpen] = useState(false);
  const [selectedRate, setSelectedRate] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [rateToDelete, setRateToDelete] = useState(null);

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  const loadRates = useCallback(async () => {
    setIsRatesLoading(true);
    try {
      const all = await getCurrencyRates();
      const list = Array.isArray(all) ? all : all.data || [];
      setRates(list.filter(r => String(r.currency_id) === String(id)));
    } catch (err) {
      console.error('Error loading rates:', err);
      showAlert('danger', 'Failed to load exchange rates.');
    } finally {
      setIsRatesLoading(false);
    }
  }, [id]);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const c = await getCurrency(id);
        setCurrency(c);
      } catch (err) {
        console.error('Error loading currency:', err);
        showAlert('danger', 'Failed to load currency.');
      } finally {
        setIsLoading(false);
      }
    };
    load();
    loadRates();
  }, [id, loadRates]);

  const handleRateSubmit = async (formData) => {
    if (!canCurrency('manage_rates')) {
      showAlert('danger', 'You do not have permission to manage exchange rates.');
      return;
    }
    try {
      if (selectedRate) {
        await updateCurrencyRate(selectedRate.id, formData);
        showAlert('success', 'Exchange rate updated successfully!');
      } else {
        await createCurrencyRate(formData);
        showAlert('success', 'Exchange rate added successfully!');
      }
      await loadRates();
      setIsRateModalOpen(false);
      setSelectedRate(null);
    } catch (err) {
      showAlert('danger', err.response?.data?.message || 'Error saving exchange rate');
    }
  };

  const rateColumns = [
    {
      key: 'exchange_rate',
      label: 'Exchange Rate (IDR)',
      render: (value) =>
        new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 6 }).format(value)
    },
    {
      key: 'start_date',
      label: 'Start Date',
      render: (value) => (value ? new Date(value).toLocaleDateString('id-ID') : '-')
    },
    {
      key: 'end_date',
      label: 'End Date',
      render: (value) => (value ? new Date(value).toLocaleDateString('id-ID') : '-')
    },
    {
      key: 'start_date',
      label: 'Status',
      render: (_, row) => {
        const today = new Date().toISOString().slice(0, 10);
        const active = row.start_date <= today && row.end_date >= today;
        return active
          ? <span className="badge badge-outline text-success">Active</span>
          : <span className="badge badge-outline text-secondary">Inactive</span>;
      }
    }
  ];

  if (isLoading) {
    return (
      <div className="container-xl d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div className="container-xl">
      {alert && (
        <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />
      )}

      {/* Header */}
      <div className="row g-2 align-items-center mb-3">
        <div className="col-auto">
          <button className="btn btn-white" onClick={() => navigate('/master-currency')}>
            <ArrowLeft size={16} className="me-1" />
            Back
          </button>
        </div>
        <div className="col">
          <h3 className="page-title m-0">Currency Detail</h3>
        </div>
        {canCurrency('manage_rates') && (
          <div className="col-auto ms-auto d-print-none">
            <button
              className="btn btn-primary"
              onClick={() => { setSelectedRate(null); setIsRateModalOpen(true); }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-plus" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M12 5l0 14" />
                <path d="M5 12l14 0" />
              </svg>
              Add Rate
            </button>
          </div>
        )}
      </div>

      {/* Currency info card */}
      {currency && (
        <div className="card mb-4">
          <div className="card-header">
            <h4 className="card-title m-0">
              {currency.currencyCode}
              <span className="text-muted fw-normal ms-2">— {currency.currencyName}</span>
            </h4>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-sm-3 col-6 mb-3">
                <div className="text-muted small mb-1">Symbol</div>
                <div className="fw-semibold fs-4">{currency.symbol}</div>
              </div>
              <div className="col-sm-3 col-6 mb-3">
                <div className="text-muted small mb-1">Decimal Places</div>
                <div className="fw-semibold">{currency.decimalPlaces ?? 2}</div>
              </div>
              <div className="col-sm-3 col-6 mb-3">
                <div className="text-muted small mb-1">Base Currency</div>
                <div>
                  {currency.isBaseCurrency
                    ? <span className="badge badge-outline text-success">Yes</span>
                    : <span className="badge badge-outline text-secondary">No</span>}
                </div>
              </div>
              <div className="col-sm-3 col-6 mb-3">
                <div className="text-muted small mb-1">Status</div>
                <div>
                  {currency.isActive
                    ? <span className="badge badge-outline text-success">Active</span>
                    : <span className="badge badge-outline text-danger">Inactive</span>}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Exchange rates table */}
      <div className="card">
        <div className="card-header">
          <h4 className="card-title m-0">Exchange Rate History</h4>
        </div>
        <div className="card-body">
          <GenericView
            data={rates}
            columns={rateColumns}
            onEdit={canCurrency('manage_rates') ? (row) => { setSelectedRate(row); setIsRateModalOpen(true); } : undefined}
            onDelete={canCurrency('manage_rates') ? (row) => { setRateToDelete(row); setShowDeleteModal(true); } : undefined}
            onView={(row) => { setSelectedRate(row); setIsRateViewModalOpen(true); }}
            isLoading={isRatesLoading}
          />
        </div>
      </div>

      {/* Rate form modal — currency pre-locked to this currency */}
      <CreateCurrencyRateForm
        isOpen={isRateModalOpen}
        onClose={() => { setIsRateModalOpen(false); setSelectedRate(null); }}
        onSubmit={handleRateSubmit}
        initialData={
          selectedRate
            ? selectedRate
            : { currency_id: currency?.id }
        }
        currencies={currency ? [currency] : []}
        lockedCurrency
      />

      <ViewCurrencyRate
        isOpen={isRateViewModalOpen}
        onClose={() => { setIsRateViewModalOpen(false); setSelectedRate(null); }}
        data={selectedRate}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setRateToDelete(null); }}
        onConfirm={async () => {
          if (!rateToDelete?.id) return;
          try {
            await deleteCurrencyRate(rateToDelete.id);
            showAlert('success', 'Exchange rate deleted successfully!');
            await loadRates();
          } catch (err) {
            console.error('Error deleting rate:', err);
            showAlert('danger', 'Failed to delete exchange rate.');
          }
          setShowDeleteModal(false);
          setRateToDelete(null);
        }}
        title="Delete Exchange Rate"
        message="Are you sure you want to delete this exchange rate? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />
    </div>
  );
};

export default CurrencyDetail;
